import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faFloppyDisk } from '@fortawesome/pro-regular-svg-icons';

const PaymentMethodConfirmationModal = NiceModal.create(
	({ ID, orderID, currency, paymentMethod, grandTotal, totalDue }) => {
		const { t } = useTranslation();
		const modal = useModal();
		const axiosPrivate = useAxiosPrivate();
		const { showError, showSuccess } = useAlert();
		const dispatch = useDispatch();

		const schema = useMemo(
			() =>
				yup
					.object()
					.shape({
						paymentMethod: yup.string().nullable().required(),
						totalDue: yup
							.number()
							.nullable()
							.when(['paymentMethod'], ([paymentMethod], schema) =>
								paymentMethod === 'cod' || paymentMethod === 'cashondelivery'
									? schema.min(1).required()
									: schema
							)
					})
					.required(),
			[]
		);

		const formMethods = useForm({
			resolver: yupResolver(schema),
			defaultValues: {
				paymentMethod: (paymentMethod === 'cashondelivery' ? 'cod' : paymentMethod) ?? null,
				grandTotal: grandTotal ?? null,
				totalDue: totalDue ?? null
			}
		});

		const {
			handleSubmit,
			watch,
			setValue,
			formState: { isSubmitting }
		} = formMethods;

		const onSubmit = async (data) => {
			await axiosPrivate
				.post(ORDER_ACTIONS.PAYMENT_METHOD_CONFIRMATION, {
					ID,
					paymentMethod: data.paymentMethod,
					totalDue: data.totalDue
				})
				.then((response) => {
					if (response.status === 200) {
						showSuccess();
						dispatch(updateDynamicListsInPage());
						modal.remove();
					} else {
						AppLogger.error(response);
					}
				})
				.catch((error) => {
					AppLogger.error(error);
					showError();
				});
		};

		return (
			<Modal maxWidth="small" fullwidth className="modal-paymentMethodConfirmation">
				<ModalTitle>
					<Typography variant="p1" noWrap>
						{t('paymentMethodConfirmationModalHeader')} (<strong>{orderID}</strong>)
					</Typography>
				</ModalTitle>
				<ModalContent>
					<Form formMethods={formMethods}>
						<FormItem>
							<FormItem label="paymentMethod" required>
								<EnumSelect
									name="paymentMethod"
									data={[
										'paid',
										'cod',
										...(paymentMethod !== 'cod' &&
										paymentMethod !== 'cashondelivery' &&
										paymentMethod !== 'paid'
											? [paymentMethod]
											: [])
									]}
									searchable={false}
									valueRemovable={false}
									placeholder="selectOne"
									value={watch('paymentMethod')}
									setValue={(value) => {
										if (value === 'paid') {
											setValue('totalDue', null, { shouldValidate: true, shouldDirty: true });
										}
										setValue('paymentMethod', value, { shouldValidate: true, shouldDirty: true });
									}}
								/>
							</FormItem>

							<FormItem label="grandTotal" required>
								<IntegerInput name="grandTotal" unit={currency} showArrows={false} disabled />
							</FormItem>

							<FormItem
								label="totalDue"
								required={
									watch('paymentMethod') === 'cod' || watch('paymentMethod') === 'cashondelivery'
								}>
								<IntegerInput name="totalDue" unit={currency} allowFloat showArrows={false} />
							</FormItem>
						</FormItem>
					</Form>
				</ModalContent>
				<ModalActions>
					<Button
						label="cancel"
						onClick={() => {
							NiceModal.show(ConfirmationModal, {
								yesAction: () => {
									modal.remove();
								}
							});
						}}
						variant="outline"
					/>
					<Button
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						icon={faFloppyDisk}
						label="confirm"
						loading={isSubmitting}
					/>
				</ModalActions>
			</Modal>
		);
	}
);

export default PaymentMethodConfirmationModal;
